/**
 * Update Notification Banner
 *
 * Polls /api/version every minute. When the version changes
 * (new deployment), shows a sticky banner prompting the user to reload.
 * Matches ai-signal's update banner design (ISV red, click-to-reload).
 *
 * Web only. Inside the Compass App (iOS and Android, utils/embedded.ts) it
 * neither polls nor renders: the app opens every page fresh and index.html is
 * served no-store, so each open already runs the latest bundle.
 */

import { useState, useEffect, useRef } from 'react';
import { isEmbedded } from '../utils/embedded';

export function UpdateBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const initialVersion = useRef<string | null>(null);

  useEffect(() => {
    if (isEmbedded()) return;

    // Fetch initial version
    fetch('/api/version')
      .then((r) => r.json())
      .then((v) => {
        if (v.version) {
          initialVersion.current = v.version;
        }
      })
      .catch(() => {});

    // Poll every minute
    const interval = setInterval(() => {
      if (!initialVersion.current) return;
      fetch('/api/version')
        .then((r) => r.json())
        .then((latest) => {
          if (latest.version && latest.version !== initialVersion.current) {
            setShowBanner(true);
          }
        })
        .catch(() => {});
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  if (!showBanner) return null;

  return (
    <div
      onClick={() => window.location.reload()}
      style={{
        background: '#CE0E2D',
        color: '#fff',
        textAlign: 'center',
        padding: '8px 16px',
        fontSize: 13,
        fontWeight: 500,
        cursor: 'pointer',
      }}
    >
      A new version is available — click to refresh
    </div>
  );
}
